// This file renders one active audit as a four-stage process rail. It derives presentation state from the durable audit phase and page counts.

import { Check } from 'lucide-react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import type { SiteHealthAuditPhase } from '@/lib/siteHealth.types'
import { useTrackerHref } from '@/provider/useTrackerHref'
import type { SiteHealthActiveAuditProps } from './siteHealthComponents.types'

const AUDIT_STEPS = ['Discover', 'Crawl', 'Audit', 'Report'] as const

type StepState = 'complete' | 'active' | 'pending'

interface StepMarkerProps {
  state: StepState
  index: number
}

function currentStep(phase: SiteHealthAuditPhase): number {
  switch (phase) {
    case 'queued':
      return 0
    case 'crawling':
      return 1
    case 'auditing':
      return 2
    case 'finalizing':
      return 3
    default:
      return 0
  }
}

function phaseLabel(phase: SiteHealthAuditPhase): string {
  switch (phase) {
    case 'queued':
      return 'Preparing audit'
    case 'crawling':
      return 'Crawling pages'
    case 'auditing':
      return 'Auditing pages'
    case 'finalizing':
      return 'Preparing report'
    default:
      return 'Audit in progress'
  }
}

function siteLabel(url: string): string {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

function stepState(index: number, activeStep: number): StepState {
  if (index < activeStep) return 'complete'

  if (index === activeStep) return 'active'

  return 'pending'
}

function stepCircleClassName(state: StepState): string {
  if (state === 'complete') return 'bg-emerald-500 text-white'

  if (state === 'active') return 'bg-primary text-primary-foreground'

  return 'bg-muted text-muted-foreground'
}

function stepStatusClassName(state: StepState): string {
  if (state === 'complete') return 'text-emerald-600'

  if (state === 'active') return 'text-primary'

  return 'text-muted-foreground'
}

function stepStatusLabel(state: StepState): string {
  if (state === 'complete') return 'Complete'

  if (state === 'active') return 'In progress'

  return 'Pending'
}

function StepMarker({ state, index }: StepMarkerProps) {
  if (state === 'complete') {
    return <Check className="size-4" aria-hidden="true" />
  }

  return index + 1
}

export default function SiteHealthActiveAudit({
  audit,
}: SiteHealthActiveAuditProps) {
  const href = useTrackerHref()
  const activeStep = currentStep(audit.phase)
  const progressTarget = Math.max(1, audit.discoveredPages)
  const progressPercent = Math.min(
    100,
    Math.round((audit.auditedPages / progressTarget) * 100)
  )

  return (
    <section
      aria-labelledby="site-health-active-heading"
      className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8"
    >
      <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)_180px] lg:items-center">
        <div>
          <h2
            id="site-health-active-heading"
            className="text-xs font-medium uppercase tracking-[0.14em] text-primary"
          >
            Audit in progress
          </h2>
          <p className="mt-3 truncate text-2xl font-semibold tracking-tight">
            {siteLabel(audit.submittedUrl)}
          </p>
          <p className="mt-4 text-base text-primary">
            {phaseLabel(audit.phase)}
          </p>
          <p className="mt-4 text-sm tabular-nums text-muted-foreground">
            {audit.auditedPages} of {audit.discoveredPages} pages audited
            {audit.failedPages > 0 ? ` · ${audit.failedPages} failed` : ''}
          </p>
        </div>

        <div>
          <ol className="grid grid-cols-4">
            {AUDIT_STEPS.map((step, index) => {
              const state = stepState(index, activeStep)
              const incomingComplete = index <= activeStep
              const outgoingComplete = index < activeStep

              return (
                <li key={step} className="relative text-center">
                  {index > 0 && (
                    <span
                      className={`absolute left-0 right-1/2 top-4 h-0.5 ${incomingComplete ? 'bg-emerald-500' : 'bg-border'}`}
                      aria-hidden="true"
                    />
                  )}
                  {index < AUDIT_STEPS.length - 1 && (
                    <span
                      className={`absolute left-1/2 right-0 top-4 h-0.5 ${outgoingComplete ? 'bg-emerald-500' : 'bg-border'}`}
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className={`relative z-10 mx-auto flex size-8 items-center justify-center rounded-full text-sm font-semibold ${stepCircleClassName(state)}`}
                  >
                    <StepMarker state={state} index={index} />
                  </span>
                  <span className="mt-3 block text-sm font-medium">{step}</span>
                  <span
                    className={`mt-1 block text-xs ${stepStatusClassName(state)}`}
                  >
                    {stepStatusLabel(state)}
                  </span>
                </li>
              )
            })}
          </ol>

          <div className="mt-6 flex items-center gap-4">
            <Progress
              value={progressPercent}
              aria-label={`${progressPercent}% audited`}
              className="h-2"
            />
            <span className="w-10 text-sm tabular-nums text-primary">
              {progressPercent}%
            </span>
          </div>
        </div>

        <Button asChild size="lg" className="h-12 w-full rounded-lg">
          <Link
            to={href(`/site-health/${audit.auditId}`)}
            aria-label={`View active audit for ${audit.submittedUrl}`}
          >
            View audit
          </Link>
        </Button>
      </div>
    </section>
  )
}
